<script setup>
import { computed, onActivated, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePolicy } from 'dashboard/composables/usePolicy';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MigrateInboxDialog from './MigrateInboxDialog.vue';
// [AstraChat] saúde/conexão + QR rápido por linha do listing
import InboxConnectionHealth from './components/InboxConnectionHealth.vue';
// [AstraChat] visões alternativas (Compacto / Kanban) + seletor
import InboxAltViews from './components/InboxAltViews.vue';
import { getInboxIdentifier, searchInboxes } from 'dashboard/helper/inbox';

const IDENTIFIER_SEPARATOR = '·';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

// [AstraChat] #155 — módulo Conexões (função personalizada) dá CRUD granular sobre
// as caixas. Admin sempre pode; função personalizada depende da ação marcada.
const { checkPermissions } = usePolicy();
const canCreateInbox = computed(
  () => isAdmin.value || checkPermissions(['connections_create'])
);
const canViewInbox = computed(
  () => isAdmin.value || checkPermissions(['connections_module'])
);
const canDeleteInbox = computed(
  () => isAdmin.value || checkPermissions(['connections_delete'])
);

const showDeletePopup = ref(false);
const showMigrate = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

// [AstraChat] modo de visualização do listing (lembrado no navegador)
const VIEW_KEY = 'inbox_view_mode';
const VIEW_OPTIONS = [
  { key: 'cards', label: 'Cards', icon: 'i-lucide-layout-grid' },
  { key: 'rows', label: 'Compacto', icon: 'i-lucide-list' },
  { key: 'kanban', label: 'Kanban', icon: 'i-lucide-columns-3' },
];
const viewMode = ref('cards');
try {
  const saved = localStorage.getItem(VIEW_KEY);
  if (saved && VIEW_OPTIONS.some(o => o.key === saved)) viewMode.value = saved;
} catch (e) {
  /* localStorage indisponível — usa o padrão */
}
const showViewMenu = ref(false);
const currentView = computed(
  () => VIEW_OPTIONS.find(o => o.key === viewMode.value) || VIEW_OPTIONS[0]
);
const setView = key => {
  viewMode.value = key;
  showViewMenu.value = false;
  try {
    localStorage.setItem(VIEW_KEY, key);
  } catch (e) {
    /* ignora */
  }
};

const inboxes = useMapGetter('inboxes/getInboxes');

onActivated(() => {
  store.dispatch('inboxes/get');
});

const inboxesList = computed(() => {
  return inboxes.value
    ?.map(inbox => ({
      ...inbox,
      channel_identifier: getInboxIdentifier(inbox),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return searchInboxes(inboxesList.value, query);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <!-- [AstraChat] seletor de visualização -->
          <div class="relative">
            <button
              type="button"
              class="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-n-weak bg-n-alpha-1 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="showViewMenu = !showViewMenu"
            >
              <span :class="currentView.icon" class="size-4 text-n-slate-11" />
              {{ currentView.label }}
              <span class="i-lucide-chevron-down size-3.5 text-n-slate-10" />
            </button>
            <div
              v-if="showViewMenu"
              class="fixed inset-0 z-10"
              @click="showViewMenu = false"
            />
            <div
              v-if="showViewMenu"
              class="absolute right-0 z-20 mt-1 w-44 py-1 rounded-lg border shadow-lg bg-n-solid-1 border-n-weak"
            >
              <button
                v-for="o in VIEW_OPTIONS"
                :key="o.key"
                type="button"
                class="flex items-center w-full gap-2 px-3 py-1.5 text-sm text-left hover:bg-n-alpha-2"
                :class="
                  o.key === viewMode ? 'text-n-brand' : 'text-n-slate-12'
                "
                @click="setView(o.key)"
              >
                <span :class="o.icon" class="size-4" />
                {{ o.label }}
                <span
                  v-if="o.key === viewMode"
                  class="i-lucide-check size-3.5 ms-auto"
                />
              </button>
            </div>
          </div>
          <Button
            v-if="isAdmin"
            :label="$t('INBOX_MGMT.MIGRATE.BUTTON')"
            icon="i-lucide-arrow-left-right"
            variant="faded"
            size="sm"
            @click="showMigrate = true"
          />
          <router-link
            v-if="isAdmin"
            :to="{ name: 'settings_inbox_whatsapp_templates' }"
          >
            <Button
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES_BULK')"
              icon="i-lucide-layout-template"
              variant="faded"
              size="sm"
            />
          </router-link>
          <router-link v-if="canCreateInbox" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <template v-else>
        <div
          v-if="viewMode === 'cards'"
          class="divide-y divide-n-weak border-t border-n-weak"
        >
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4 min-w-0 flex-1">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1 min-w-0">
              <span
                :title="inbox.name"
                class="block text-heading-3 text-n-slate-12 capitalize truncate max-w-full"
              >
                {{ inbox.name }}
              </span>
              <div
                class="flex items-center gap-1 min-w-0 max-w-full text-body-main text-n-slate-11"
              >
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  :voice-enabled="inbox.voice_enabled"
                  class="shrink-0"
                />
                <template v-if="inbox.channel_identifier">
                  <span aria-hidden="true">{{ IDENTIFIER_SEPARATOR }}</span>
                  <bdi
                    dir="auto"
                    :title="inbox.channel_identifier"
                    class="truncate"
                    data-test-id="channel-identifier"
                  >
                    {{ inbox.channel_identifier }}
                  </bdi>
                </template>
              </div>
            </div>
          </div>
          <div class="flex gap-3 justify-end items-center shrink-0">
            <InboxConnectionHealth :inbox="inbox" />
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="canViewInbox"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="canDeleteInbox"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
        </div>
        <InboxAltViews
          v-else
          :inboxes="filteredInboxesList"
          :mode="viewMode"
          :is-admin="isAdmin"
          :can-view="canViewInbox"
          :can-delete="canDeleteInbox"
          @delete="openDelete"
        />
      </template>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <MigrateInboxDialog
      v-if="showMigrate"
      @close="showMigrate = false"
      @migrated="showMigrate = false"
    />
  </SettingsLayout>
</template>
